import { useCallback } from 'react'
import type { DataNode } from '../types'
import { useContentItemViewNode } from './useContentItemViewNodeService'

/**
 * Custom hook to handle actions related to Paragraph content items
 * @returns Actions related to Paragraph content items
 */
export const useParagraphActions = () => {
    const {
        contentItemViewNodes,
        findContentItemNode,
        insertNodeAfterSibling,
        setAddingNode,
        createContentItemViewNode,
    } = useContentItemViewNode()

    /**
     * Adds a Paragraph content item after a sibling content item
     * @param sibling Sibling content item to insert this node after
     */
    const addParagraphAfterSibling = useCallback(
        (sibling: DataNode) => {
            const siblingNode = findContentItemNode(sibling.key)

            if (!siblingNode) return

            // Save new node and reorder siblings to Local State
            const paragraphNode = createContentItemViewNode('', siblingNode)
            // Tables have no textType/type of their own, so the new node would
            // otherwise inherit the table's and get misidentified as another
            // Table by TreeDataMapper, rendering nothing instead of a paragraph.
            if (sibling.type === 'Table') {
                paragraphNode.textType = 'Paragraph'
                paragraphNode.type = undefined
            }
            insertNodeAfterSibling(paragraphNode, siblingNode.id)
            // Set currently being added node to focus
            setAddingNode(paragraphNode)
        },
        [
            contentItemViewNodes,
            findContentItemNode,
            createContentItemViewNode,
            insertNodeAfterSibling,
        ]
    )

    /**
     * Adds the first Paragraph inside an empty user-added Article (manual "+" path).
     * dtvContentItemParentId is set to the article's own id so ParagraphInputBuilder
     * routes through the ContentItem parent path rather than the DTV slot path.
     */
    const addFirstParagraphToArticle = useCallback(
        (article: DataNode) => {
            const articleNode = findContentItemNode(article.key)
            if (!articleNode) return

            // Keep textType: 'Article' so createContentItemViewNode injects the placeholder
            // element, then override to 'Paragraph' on the result for ParagraphInputBuilder routing.
            const builtNode = createContentItemViewNode('', {
                ...articleNode,
                dtvContentItemParentId: articleNode.id,
            })
            const paragraphNode = { ...builtNode, textType: 'Paragraph' }
            insertNodeAfterSibling(paragraphNode, articleNode.id)
            setAddingNode(paragraphNode)
        },
        [
            findContentItemNode,
            createContentItemViewNode,
            insertNodeAfterSibling,
            setAddingNode,
        ]
    )

    return { addParagraphAfterSibling, addFirstParagraphToArticle }
}
